import { observable, reaction, makeObservable } from 'mobx';
import { GlobalStatesV3 } from './GlobalStatesV3';
import { parseQueryParams, serializeQueryParams } from '../../mainv3/types/location';
import autobind from 'autobind-decorator';

export class UrlBoundParam<T> {
  paramName = null;
  value: T = null;
  onInitialize = null;

  constructor(
    paramName: string,
    defaultValue: T,
    serializer: (value: T) => string,
    deserializer: (rawValue: string) => T,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onInitialize?: (value: T) => any
  ) {
    makeObservable(this, {
      value: observable,
    });

    this.paramName = paramName;
    const params = parseQueryParams(GlobalStatesV3.routing.location);
    this.onInitialize = onInitialize;
    if (params[paramName]) {
      this.value = deserializer(params[paramName]);
    } else {
      this.value = defaultValue;
    }
    if (this.onInitialize) {
      this.onInitialize(this.value);
    }
    reaction(
      () => this.value,
      (v) => {
        const currentParams = parseQueryParams(GlobalStatesV3.routing.location);
        const newParams = { ...currentParams, [paramName]: serializer(v) };
        if (v === undefined || v === null) {
          delete newParams[paramName];
        }
        GlobalStatesV3.routing.replace({
          ...GlobalStatesV3.routing.location,
          search: serializeQueryParams(newParams),
        });
      }
    );
  }

  @autobind
  set(value: T) {
    this.value = value;
  }

  get(): T {
    return this.value;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static integerParam(paramName: string, defaultValue: number, onInitialize?: (v) => any) {
    return new UrlBoundParam<number>(
      paramName,
      defaultValue,
      (v) => {
        if (Number.isFinite(v)) {
          return v.toString();
        } else {
          return undefined;
        }
      },
      (rawValue) => {
        const parsed = Number(rawValue);
        if (Number.isFinite(parsed)) {
          return parsed;
        } else {
          return defaultValue;
        }
      },
      onInitialize
    );
  }

  static stringParam(paramName: string, defaultValue: string) {
    return new UrlBoundParam<string>(
      paramName,
      defaultValue,
      (v) => v,
      (v) => v
    );
  }
  static numericArrayParam(paramName: string, defaultValue: number[]) {
    return new UrlBoundParam<number[]>(
      paramName,
      defaultValue,
      (v) => v.join(','),
      (v) =>
        v
          .split(',')
          .map((i) => Number(i))
          .filter((i) => Number.isFinite(i))
    );
  }
}
